import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminLogin, adminGetMe, setAuthToken } from '../api/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('admin_token');
    const storedUser = localStorage.getItem('admin_user');

    if (storedToken) {
      setToken(storedToken);
      setAuthToken(storedToken);
      // Affiche tout de suite le profil mémorisé pour éviter un clignotement…
      if (storedUser) {
        try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
      }
      // …puis rafraîchit depuis le serveur, qui fait autorité sur le rôle.
      adminGetMe()
        .then((moi) => {
          setUser(moi);
          localStorage.setItem('admin_user', JSON.stringify(moi));
        })
        .catch(() => { /* 401 : l'intercepteur nettoie et redirige vers le login */ })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await adminLogin(email, password);
      const { access_token } = response;

      setToken(access_token);
      setAuthToken(access_token);
      localStorage.setItem('admin_token', access_token);

      // Récupère le profil complet (rôle et nom inclus) juste après l'authentification.
      let profil = { email };
      try {
        profil = await adminGetMe();
      } catch { /* garde le minimum si /admin/me échoue */ }
      setUser(profil);
      localStorage.setItem('admin_user', JSON.stringify(profil));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  };

  const value = {
    user,
    token,
    role: user?.role || null,
    roleLabel: user?.role_label || '',
    permissions: user?.permissions || [],
    hasPerm: (p) => (user?.permissions || []).includes(p),
    login,
    logout,
    isAuthenticated: !!token,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
